import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { rupiah, fmtDateTime } from "../lib/format";
import { RupiahInput } from "../components/RupiahInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Trash2, Truck, Undo2, CheckCircle2, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Purchases() {
  const { isViewOnly, can } = useAuth();
  const { refreshLowStock } = useApp();
  const [purchases, setPurchases] = useState([]);
  const [returns, setReturns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [md, setMd] = useState({ units: [], categories: [], subcategories: [], locations: [] });
  const [filter, setFilter] = useState("");
  const [payStatus, setPayStatus] = useState("all");
  const [retFilter, setRetFilter] = useState("");
  const [poOpen, setPoOpen] = useState(false);
  const [retForm, setRetForm] = useState(null);
  const [supForm, setSupForm] = useState(null);
  const [poDetail, setPoDetail] = useState(null);
  const [retDetail, setRetDetail] = useState(null);

  const load = () => {
    api.get("/purchases").then((r) => setPurchases(r.data));
    api.get("/purchase-returns").then((r) => setReturns(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/items").then((r) => setItems(r.data));
    Promise.all(["units", "categories", "subcategories", "locations"].map((c) => api.get(`/${c}`)))
      .then(([u, c, s, l]) => setMd({ units: u.data, categories: c.data, subcategories: s.data, locations: l.data }));
  };
  useEffect(() => { load(); }, []);

  const barcodeOf = (itemId) => items.find((x) => x.id === itemId)?.barcode || "";
  const filtered = purchases.filter((p) => {
    const q = filter.toLowerCase();
    const matchQ = !q || p.number.toLowerCase().includes(q) || (p.nota || "").toLowerCase().includes(q) ||
      p.supplier.toLowerCase().includes(q) ||
      p.items.some((i) => i.name.toLowerCase().includes(q) || barcodeOf(i.item_id).toLowerCase().includes(q));
    const matchS = payStatus === "all" || p.payment_status === payStatus;
    return matchQ && matchS;
  }).sort((a, b) => {
    const au = a.payment_status === "lunas" ? 1 : 0;
    const bu = b.payment_status === "lunas" ? 1 : 0;
    return au - bu;
  });
  const filteredReturns = returns.filter((r) => {
    const q = retFilter.toLowerCase();
    return !q || (r.supplier || "").toLowerCase().includes(q) || (r.item_name || "").toLowerCase().includes(q) ||
      barcodeOf(r.item_id).toLowerCase().includes(q);
  });

  const pay = async (id) => { if (!window.confirm("Lunasi pembelian ini? Nilai pelunasan akan dicatat sebagai pengeluaran di Buku Kas.")) return; await api.put(`/purchases/${id}/pay`); toast.success("Ditandai lunas"); load(); };
  const saveSup = async () => {
    try { await api.post("/suppliers", supForm); toast.success("Supplier disimpan"); setSupForm(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveRet = async () => {
    try { await api.post("/purchase-returns", retForm); toast.success("Retur tersimpan"); setRetForm(null); load(); refreshLowStock(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="purchases-page">
      <div>
        <h1 className="text-2xl font-bold font-display text-slate-900">Pembelian & Supplier</h1>
        <p className="text-sm text-slate-500">Pengadaan barang, retur, dan data supplier</p>
      </div>

      <Tabs defaultValue="po">
        <TabsList>
          <TabsTrigger value="po" data-testid="tab-po">Pembelian</TabsTrigger>
          <TabsTrigger value="returns" data-testid="tab-returns">Retur Pembelian</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Supplier</TabsTrigger>
        </TabsList>

        <TabsContent value="po" className="space-y-4">
          <div className="flex justify-between gap-3 flex-wrap">
            <div className="flex gap-3 flex-wrap flex-1">
              <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Cari: no. nota / supplier / barcode / nama barang..." className="max-w-sm" data-testid="purchase-filter-input" />
              <Select value={payStatus} onValueChange={setPayStatus}>
                <SelectTrigger className="w-44" data-testid="purchase-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  <SelectItem value="belum_lunas">Belum Lunas</SelectItem>
                  <SelectItem value="lunas">Lunas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isViewOnly && <Button onClick={() => setPoOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="add-purchase-button"><Plus className="h-4 w-4 mr-1.5" /> Pembelian Baru</Button>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>No. Pembelian</TableHead><TableHead>Nota Fisik</TableHead><TableHead>Supplier</TableHead>
                <TableHead>Tanggal</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs font-semibold">{p.number}</TableCell>
                    <TableCell className="text-xs">{p.nota || "-"}</TableCell>
                    <TableCell>{p.supplier}</TableCell>
                    <TableCell className="text-xs text-slate-500">{fmtDateTime(p.created_at)}</TableCell>
                    <TableCell className="text-right mono font-semibold">{rupiah(p.total)}</TableCell>
                    <TableCell>
                      <Badge className={p.payment_status === "lunas" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-rose-100 text-rose-700 hover:bg-rose-100"}>
                        {p.payment_status === "lunas" ? "Lunas" : "Belum Lunas"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <Button size="sm" variant="ghost" onClick={() => setPoDetail(p)} data-testid={`view-purchase-${p.number}`}><Eye className="h-4 w-4 mr-1" /> Lihat</Button>
                        {!isViewOnly && p.payment_status !== "lunas" && <Button size="sm" variant="outline" onClick={() => pay(p.id)}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Lunasi</Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Belum ada pembelian</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="returns" className="space-y-4">
          <div className="flex justify-between gap-3 flex-wrap">
            <Input value={retFilter} onChange={(e) => setRetFilter(e.target.value)} placeholder="Cari: supplier / barcode / nama barang..." className="max-w-sm" data-testid="return-filter-input" />
            {!isViewOnly && <Button onClick={() => setRetForm({ supplier: "", item_id: "", qty: 1, reason: "" })} className="bg-orange-500 hover:bg-orange-600" data-testid="add-return-button"><Undo2 className="h-4 w-4 mr-1.5" /> Retur Baru</Button>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Tanggal</TableHead><TableHead>Supplier</TableHead><TableHead>Barang</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Alasan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredReturns.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs text-slate-500">{fmtDateTime(r.created_at)}</TableCell>
                    <TableCell>{r.supplier}</TableCell><TableCell>{r.item_name}</TableCell>
                    <TableCell className="text-right mono">{r.qty}</TableCell><TableCell className="text-xs">{r.reason}</TableCell>
                    <TableCell><Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{r.status}</Badge></TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setRetDetail(r)} data-testid={`view-return-${r.id}`}><Eye className="h-4 w-4 mr-1" /> Lihat</Button></TableCell>
                  </TableRow>
                ))}
                {filteredReturns.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Belum ada retur</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4">
          {!isViewOnly && <Button onClick={() => setSupForm({ name: "", phone: "", address: "" })} className="bg-orange-500 hover:bg-orange-600" data-testid="add-supplier-button"><Plus className="h-4 w-4 mr-1.5" /> Supplier Baru</Button>}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Nama</TableHead><TableHead>Telepon</TableHead><TableHead>Alamat</TableHead></TableRow></TableHeader>
              <TableBody>
                {suppliers.map((s) => (
                  <TableRow key={s.id}><TableCell className="font-medium">{s.name}</TableCell><TableCell>{s.phone || "-"}</TableCell><TableCell className="text-slate-500">{s.address || "-"}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <PurchaseDialog open={poOpen} setOpen={setPoOpen} suppliers={suppliers} items={items} md={md} onDone={() => { load(); refreshLowStock(); }} />

      {/* Purchase detail */}
      <Dialog open={!!poDetail} onOpenChange={(o) => !o && setPoDetail(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-mono">{poDetail?.number}</DialogTitle></DialogHeader>
          {poDetail && (
            <div className="space-y-3" data-testid="purchase-detail">
              <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                <span>{fmtDateTime(poDetail.created_at)}</span>
                <span>Supplier: <b className="text-slate-700">{poDetail.supplier}</b></span>
                <span>Nota: <b className="text-slate-700">{poDetail.nota || "-"}</b></span>
                <span>Status: <b className={poDetail.payment_status === "lunas" ? "text-emerald-600" : "text-rose-600"}>{poDetail.payment_status === "lunas" ? "Lunas" : "Belum Lunas"}</b></span>
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50"><TableHead>Barang</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Harga Netto</TableHead><TableHead className="text-right">Subtotal</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {poDetail.items.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm">{l.name}</TableCell>
                        <TableCell className="text-right mono">{l.qty}</TableCell>
                        <TableCell className="text-right mono text-slate-500">{rupiah(l.price_buy)}</TableCell>
                        <TableCell className="text-right mono font-semibold">{rupiah(l.subtotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between font-bold text-base border-t pt-2"><span>Total</span><span className="mono text-orange-600">{rupiah(poDetail.total)}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Return detail */}
      <Dialog open={!!retDetail} onOpenChange={(o) => !o && setRetDetail(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Detail Retur</DialogTitle></DialogHeader>
          {retDetail && (
            <div className="space-y-2 text-sm" data-testid="return-detail">
              <div className="flex justify-between"><span className="text-slate-500">Tanggal</span><span>{fmtDateTime(retDetail.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Supplier</span><span className="font-medium">{retDetail.supplier}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Barang</span><span className="font-medium">{retDetail.item_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Qty</span><span className="mono">{retDetail.qty}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Alasan</span><span>{retDetail.reason || "-"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span><Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{retDetail.status}</Badge></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Return dialog */}
      <Dialog open={!!retForm} onOpenChange={(o) => !o && setRetForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Retur Pembelian</DialogTitle></DialogHeader>
          {retForm && <div className="space-y-3">
            <div className="space-y-1"><Label>Supplier</Label>
              <Select value={retForm.supplier || undefined} onValueChange={(v) => setRetForm({ ...retForm, supplier: v })}>
                <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>Barang</Label>
              <Select value={retForm.item_id || undefined} onValueChange={(v) => setRetForm({ ...retForm, item_id: v })}>
                <SelectTrigger data-testid="return-item-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent className="max-h-64">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>Qty</Label><Input type="number" value={retForm.qty} onChange={(e) => setRetForm({ ...retForm, qty: Number(e.target.value) })} /></div>
            <div className="space-y-1"><Label>Alasan</Label><Input value={retForm.reason} onChange={(e) => setRetForm({ ...retForm, reason: e.target.value })} /></div>
          </div>}
          <DialogFooter><Button onClick={saveRet} className="bg-orange-500 hover:bg-orange-600" data-testid="save-return-button">Simpan Retur</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier dialog */}
      <Dialog open={!!supForm} onOpenChange={(o) => !o && setSupForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Supplier Baru</DialogTitle></DialogHeader>
          {supForm && <div className="space-y-3">
            <div className="space-y-1"><Label>Nama</Label><Input value={supForm.name} onChange={(e) => setSupForm({ ...supForm, name: e.target.value })} data-testid="supplier-name-input" /></div>
            <div className="space-y-1"><Label>Telepon</Label><Input value={supForm.phone} onChange={(e) => setSupForm({ ...supForm, phone: e.target.value })} /></div>
            <div className="space-y-1"><Label>Alamat</Label><Input value={supForm.address} onChange={(e) => setSupForm({ ...supForm, address: e.target.value })} /></div>
          </div>}
          <DialogFooter><Button onClick={saveSup} className="bg-orange-500 hover:bg-orange-600" data-testid="save-supplier-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PurchaseDialog({ open, setOpen, suppliers, items, md, onDone }) {
  const [supplier, setSupplier] = useState("");
  const [nota, setNota] = useState("");
  const [status, setStatus] = useState("belum_lunas");
  const [lines, setLines] = useState([]);

  const addLine = () => setLines([...lines, { item_id: "", barcode: "", name: "", qty: 1, price_list: 0, disc1: 0, disc2: 0, disc3: 0, isNew: false, unit: "", category: "", subcategory: "", location: "", price_sell: 0, min_stock: 0 }]);
  const upd = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const rm = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const pickItem = (i, id) => {
    const it = items.find((x) => x.id === id);
    upd(i, { item_id: id, name: it?.name || "", price_list: it?.price_buy || 0, barcode: it?.barcode || "" });
  };
  const nettoOf = (l) => (l.price_list || 0) * (1 - (l.disc1 || 0) / 100) * (1 - (l.disc2 || 0) / 100) * (1 - (l.disc3 || 0) / 100);
  const totalDiscOf = (l) => (l.price_list ? (1 - nettoOf(l) / l.price_list) * 100 : 0);
  const total = lines.reduce((s, l) => s + l.qty * nettoOf(l), 0);

  const save = async () => {
    if (!supplier || !lines.length) { toast.error("Lengkapi supplier & item"); return; }
    try {
      await api.post("/purchases", {
        supplier, nota, payment_status: status,
        items: lines.map((l) => ({
          item_id: l.isNew ? null : l.item_id, barcode: l.barcode, name: l.name,
          qty: l.qty, price_buy: nettoOf(l), price_list: l.price_list || 0,
          disc1: l.disc1 || 0, disc2: l.disc2 || 0, disc3: l.disc3 || 0,
          price_sell: l.price_sell || 0, min_stock: l.min_stock || 0,
          unit: l.unit || "", category: l.category || "", subcategory: l.subcategory || "", location: l.location || "",
        })),
      });
      toast.success("Pembelian tersimpan, stok bertambah");
      setOpen(false); setSupplier(""); setNota(""); setLines([]); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="h-4 w-4" /> Form Pembelian</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1"><Label>Supplier</Label>
            <Select value={supplier || undefined} onValueChange={setSupplier}>
              <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label>No. Nota Fisik</Label><Input value={nota} onChange={(e) => setNota(e.target.value)} data-testid="po-nota-input" /></div>
          <div className="space-y-1"><Label>Status Bayar</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="belum_lunas">Belum Lunas</SelectItem><SelectItem value="lunas">Lunas</SelectItem></SelectContent>
            </Select></div>
        </div>

        <div className="space-y-2 mt-2">
          {lines.map((l, i) => (
            <div key={i} className={`rounded-lg border-2 p-3 space-y-2 ${i % 2 === 0 ? "border-slate-200 bg-white" : "border-orange-200 bg-orange-50/50"}`} data-testid={`po-line-${i}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white bg-orange-500 rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center">{i + 1}</span>
                <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={l.isNew} onChange={(e) => upd(i, { isNew: e.target.checked, item_id: "", name: "" })} className="accent-orange-500" /> Barang baru</label>
                <button onClick={() => rm(i)} className="ml-auto"><Trash2 className="h-4 w-4 text-rose-400" /></button>
              </div>
              {l.isNew ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div><Label className="text-xs">Barcode</Label><Input placeholder="Barcode" value={l.barcode} onChange={(e) => upd(i, { barcode: e.target.value })} data-testid={`po-new-barcode-${i}`} /></div>
                  <div><Label className="text-xs">Nama Barang</Label><Input placeholder="Nama barang" value={l.name} onChange={(e) => upd(i, { name: e.target.value })} data-testid={`po-new-name-${i}`} /></div>
                  <POCombo label="Satuan" value={l.unit} onChange={(v) => upd(i, { unit: v })} options={md?.units || []} testid={`po-new-unit-${i}`} />
                  <POCombo label="Kategori" value={l.category} onChange={(v) => upd(i, { category: v })} options={md?.categories || []} testid={`po-new-category-${i}`} />
                  <POCombo label="Sub-Kategori" value={l.subcategory} onChange={(v) => upd(i, { subcategory: v })} options={md?.subcategories || []} testid={`po-new-subcategory-${i}`} />
                  <POCombo label="Lokasi" value={l.location} onChange={(v) => upd(i, { location: v })} options={md?.locations || []} testid={`po-new-location-${i}`} />
                  <div><Label className="text-xs">Harga Jual</Label><RupiahInput value={l.price_sell} onChange={(v) => upd(i, { price_sell: v })} data-testid={`po-new-sell-${i}`} /></div>
                  <div><Label className="text-xs">Stok Minimum</Label><Input type="number" value={l.min_stock} onChange={(e) => upd(i, { min_stock: Number(e.target.value) })} data-testid={`po-new-minstock-${i}`} /></div>
                </div>
              ) : (
                <Select value={l.item_id || undefined} onValueChange={(v) => pickItem(i, v)}>
                  <SelectTrigger data-testid={`po-item-select-${i}`}><SelectValue placeholder="Pilih barang" /></SelectTrigger>
                  <SelectContent className="max-h-64">{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name} ({it.barcode})</SelectItem>)}</SelectContent>
                </Select>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div><Label className="text-xs">Qty</Label><Input type="number" value={l.qty} onChange={(e) => upd(i, { qty: Number(e.target.value) })} data-testid={`po-qty-${i}`} /></div>
                <div><Label className="text-xs">Harga Price List</Label><RupiahInput value={l.price_list} onChange={(v) => upd(i, { price_list: v })} data-testid={`po-pricelist-${i}`} /></div>
                <div><Label className="text-xs">Diskon 1 (%)</Label><Input type="number" value={l.disc1} onChange={(e) => upd(i, { disc1: Number(e.target.value) })} data-testid={`po-disc1-${i}`} /></div>
                <div><Label className="text-xs">Diskon 2 (%)</Label><Input type="number" value={l.disc2} onChange={(e) => upd(i, { disc2: Number(e.target.value) })} data-testid={`po-disc2-${i}`} /></div>
                <div><Label className="text-xs">Diskon 3 (%)</Label><Input type="number" value={l.disc3} onChange={(e) => upd(i, { disc3: Number(e.target.value) })} data-testid={`po-disc3-${i}`} /></div>
              </div>
              <div className="grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-2 text-sm">
                <div><div className="text-[11px] text-slate-500">Total Diskon</div><div className="mono font-semibold text-rose-600" data-testid={`po-totaldisc-${i}`}>{totalDiscOf(l).toFixed(2)}%</div></div>
                <div><div className="text-[11px] text-slate-500">Harga Netto/unit</div><div className="mono font-semibold text-slate-900" data-testid={`po-netto-${i}`}>{rupiah(nettoOf(l))}</div></div>
                <div><div className="text-[11px] text-slate-500">Subtotal</div><div className="mono font-bold text-orange-600" data-testid={`po-subtotal-${i}`}>{rupiah(l.qty * nettoOf(l))}</div></div>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addLine} data-testid="po-add-line-button" className="w-full"><Plus className="h-4 w-4 mr-1.5" /> Tambah Item</Button>
        </div>

        <div className="flex items-center justify-between border-t pt-3">
          <span className="font-semibold">Total Harga Netto</span>
          <span className="text-xl font-bold mono text-orange-600" data-testid="po-grand-total">{rupiah(total)}</span>
        </div>
        <DialogFooter><Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="save-purchase-button">Simpan Pembelian</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function POCombo({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={`Pilih ${label}`} /></SelectTrigger>
        <SelectContent className="max-h-64">
          {options.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
